/* global document, window, fetch, setTimeout */
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {defineMessages, injectIntl, FormattedMessage, IntlShape} from 'react-intl';
import moment from 'moment';
import "./Logger.scss";
import RefreshButton from '../jobs/RefreshButton.react';
import Convert from 'ansi-to-html';
import SafeHTML from '../fw/SafeHTML.react';
import { Logger } from '../../common/types/types';
import Token from '../../../packages/extension/src/background/Token';
import {displayError} from '../app/errorDisplayer';

const regex = /^[[:blank:]]*([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2})[[:blank:]]+(.*)/mg;

const messages = defineMessages({
    downloadLog: {id: 'logger.downloadLog', defaultMessage: 'Download log'},
});

export function escapeXML(str) {
	if (str === undefined) {
		return '';
	}
	return String(str).replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;');
}

interface LoggerViewProps {
    intl: IntlShape;
    logger: Logger;
    relay: object;
    parentRelay?: object;
}

class LoggerView extends React.Component<LoggerViewProps> {

    typeName = 'loggers';

    constructor(props) {
        super(props);
    }

    static propTypes = {
        intl: PropTypes.object.isRequired,
        logger: PropTypes.object.isRequired,
        relay: PropTypes.object.isRequired,
        parentRelay: PropTypes.object,
        downloadUrl: PropTypes.string,
    };

    parseFilenameFromContentDisposition(headerValue) {
        if (!headerValue) {
            return null;
        }
        let m = headerValue.match(/filename="([^"]+)"/);
        return m ? m[1] : null;
    }

    downloadLogs = () => {
        const { downloadUrl, logger } = this.props;
        if (!downloadUrl) {
            return Promise.resolve();
        }

        const hasLogs = Boolean(logger && logger.edges && logger.edges.length > 0);
        if (!hasLogs) {
            return Promise.resolve();
        }

        try {
            let token = Token.get();
            if (token && !token.startsWith('Bearer ')) {
                token = 'Bearer ' + token;
            }

            if (!token) {
                displayError(new Error("Missing auth token"));
                return Promise.resolve();
            }

            return fetch(downloadUrl, {
                method: 'GET',
                headers: {
                    'Authorization': token,
                }
            }).then((response) => {
                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('text/html')) {
                    throw new Error('Download returned HTML (likely wrong API host / missing proxy to backend).');
                }
                if (!response.ok) {
                    throw new Error('Download failed (' + response.status + ')');
                }
                let filename = this.parseFilenameFromContentDisposition(response.headers.get('content-disposition')) || 'logs.log';
                return response.blob().then((blob) => ({blob, filename}));
            }).then(({blob, filename}) => {
                let blobUrl = window.URL.createObjectURL(blob);
                let a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 1000);
            }).catch((e) => {
                displayError(e);
            });
        } catch (e) {
            displayError(e);
            return Promise.resolve();
        }
    };

    render() {
        let { logger } = this.props;
        let convert = new Convert();
        const hasLogs = Boolean(logger && logger.edges && logger.edges.length > 0);

        let result = [];
        let idx = 0;
        for (let log of logger.edges) {
            if (log.node) {
                let title = null;
                let style = {};

                for (let row of log.node.message.split("\n")) {
                    let prefix = "";
                    for (let i = 0; i < row.length; i++) {
                        if (row.charCodeAt(i) === ' '.charCodeAt(0)) {
                            prefix += "&nbsp;";
                        } else {
                            break;
                        }
                    }

                    let m = regex.exec(row.trim());
                    if (m) {
                        let type = m[2];
                        let dt = m[1];
                        row = m[3];
                        if (type === "ERROR" || type === 'error') {
                            style['color'] = '#faa';
                        } else if (type === "DEBUG" || type === 'debug') {
                            style['color'] = '#afa';
                        } else if (type === "WARN" || type === 'warn') {
                            style['color'] = '#ffa';
                        }

                        title = moment(dt).format("DD.MM.YYYY HH:mm:ss") + " <" + type + ">";
                    }

                    row = escapeXML(row);
                    result.push(<div key={idx++} style={style}><SafeHTML title={title} value={convert.toHtml(prefix + row, {newline: true, escapeXML: true})} /></div>);
                }
            }
        }

        if (result.length === 0) {
            result = <div style={{'color': '#fff', 'textAlign': 'center', 'marginTop': '3em'}}><strong><FormattedMessage defaultMessage="No logs ..." id="logger.empty" /></strong></div>;
        }

        return (
            <div className="logger">
                <RefreshButton className="btn-sm loggerRefreshButton" fastRefresh relay={this.props.parentRelay || this.props.relay} />
                {this.props.downloadUrl ?
                    <button
                        className="btn btn-default btn-sm loggerDownloadButton"
                        disabled={!hasLogs}
                        onClick={this.downloadLogs}
                        title={this.props.intl.formatMessage(messages.downloadLog)}
                        type="button"
                    >
                        <i className="fa fa-download" />
                    </button>
                : null}
                <div className="loggerScrollable">
                    <div className="loggerContent">
                        {result}
                    </div>
                </div>
            </div>
        );
    }
}

export default Relay.createContainer(injectIntl(LoggerView), {
    initialVariables: {
    },
    prepareVariables (variables) {
        return variables;
    },
    fragments: {
        logger: () => Relay.QL`
            fragment on loggersConnection {
                edges {
                    node {
                        id,
                        realId,
                        message,
                        createdAt,
                    }
                }
            }
        `,
    },
});
